/**
 * Wizard step 3 — access class, public visibility and the raster categorical flag (M6 §5).
 *
 * Radio *cards* rather than a select: the three classes differ by who may see the data, and
 * that difference has to be readable at the moment of the choice, not in a tooltip. The
 * cards are a real `radiogroup` of native inputs, so keyboard and screen-reader behaviour
 * is the platform's.
 */
import { useTranslation } from 'react-i18next';

import {
  CLASSIFICATIONS,
  type Classification,
  type MetadataForm as Form,
  type WizardState,
} from './manager';

export interface ClassificationStepProps {
  state: WizardState;
  onChange: (patch: Partial<Form>) => void;
}

export function ClassificationStep({ state, onChange }: ClassificationStepProps) {
  const { t } = useTranslation();
  const f = state.form;
  // Only a raster has pixel values to be categorical about; the flag is meaningless for a
  // Shapefile or a CSV, so it is not offered there rather than offered and ignored.
  const isRaster = state.row?.format === 'geotiff';

  return (
    <div className="mgr-step" data-testid="mgr-step-3">
      <fieldset className="mgr-cls" data-testid="mgr-f-classification">
        <legend className="mgr-label">{t('manager.f.classification')}</legend>
        <div className="mgr-cls-cards">
          {CLASSIFICATIONS.map((cls: Classification) => (
            <label
              key={cls}
              className={f.classification === cls ? `mgr-cls-card on ${cls}` : `mgr-cls-card ${cls}`}
              htmlFor={`mgr-f-classification-${cls}`}
            >
              <input
                type="radio"
                id={`mgr-f-classification-${cls}`}
                data-testid={`mgr-f-classification-${cls}`}
                name="mgr-classification"
                value={cls}
                checked={f.classification === cls}
                onChange={() => onChange({ classification: cls })}
              />
              <span className="mgr-cls-name">{t(`manager.cls.${cls}`)}</span>
              <span className="mgr-cls-blurb">{t(`manager.cls.${cls}Blurb`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="mgr-field" htmlFor="mgr-f-websiteVisible">
        <span className="mgr-label">{t('manager.f.websiteVisible')}</span>
        <select
          id="mgr-f-websiteVisible"
          data-testid="mgr-f-websiteVisible"
          className="mgr-input"
          value={f.websiteVisible ? 'yes' : 'no'}
          onChange={(e) => onChange({ websiteVisible: e.target.value === 'yes' })}
        >
          <option value="yes">{t('manager.visible.yes')}</option>
          <option value="no">{t('manager.visible.no')}</option>
        </select>
      </label>

      {isRaster ? (
        <label className="mgr-check" htmlFor="mgr-f-isCategorical">
          <input
            type="checkbox"
            id="mgr-f-isCategorical"
            data-testid="mgr-f-isCategorical"
            checked={f.isCategorical}
            onChange={(e) => onChange({ isCategorical: e.target.checked })}
          />
          <span>
            <b>{t('manager.f.isCategorical')}</b>
            <span className="mgr-hint">{t('manager.f.categoricalHint')}</span>
          </span>
        </label>
      ) : null}
    </div>
  );
}
